import type { ActivityType } from '@/types';
import type { TraceKind, TraceValue } from './trace';

/**
 * GRUNDO rögzítés — közös típusok.
 *
 * A modul szándékosan nem ismer sem böngészőt, sem natív platformot: a
 * pozíció forrása egy interfész mögött van. Ennek nem elvi oka van, hanem
 * gyakorlati — a böngészőben nem lehet háttérben mérni (a `watchPosition`
 * leáll, amint az oldal nem látható), ezért előbb-utóbb natív burok kell,
 * és akkor csak ezt az egy interfészt akarjuk lecserélni.
 */

/**
 * Egy nyers helymeghatározás.
 *
 * Szándékosan a `TracePoint` bővítése (`src/types`): minden mező, amit a
 * játékmotor vár, ugyanazon a néven szerepel benne, tehát egy elfogadott
 * minta átalakítás nélkül átadható a motornak.
 */
export interface PositionSample {
  lat: number;
  lng: number;
  /** epoch ms */
  t: number;
  /** vízszintes pontosság méterben — a szűrés legfontosabb bemenete */
  accuracy: number;
  /** méter a tengerszint felett, ha az eszköz jelenti */
  elevation?: number;
  /** m/s az eszköz szerint. Nem hiszünk el vakon, csak jelzésnek használjuk. */
  speed?: number;
}

export type TrackingErrorCode =
  /** A felhasználó megtagadta a helyhozzáférést. */
  | 'permission_denied'
  /** Nincs helymeghatározás (nincs GPS, ki van kapcsolva, nincs jel). */
  | 'unavailable'
  /** Nem érkezett fix a megadott időn belül. */
  | 'timeout'
  /** A böngésző csak biztonságos eredeten ad helyet (https vagy localhost). */
  | 'insecure_context'
  /** A futtatókörnyezet egyáltalán nem ismeri a helymeghatározást. */
  | 'unsupported';

export class TrackingError extends Error {
  constructor(
    public readonly code: TrackingErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'TrackingError';
  }
}

export interface PositionHandlers {
  onSample(sample: PositionSample): void;
  onError(error: TrackingError): void;
}

/** A natív zárolt képernyős méréshez szükséges, kis méretű állapotpillanatkép. */
export interface PositionActivityState {
  startedAt: number;
  distanceM: number;
  pausedMs: number;
  pausedAt: number | null;
  status: 'recording' | 'paused';
  /**
   * Only while paused. An `auto` pause must keep the GPS running: the
   * automatic resume is detected from new samples, so a source that stops
   * locating on pause would leave the activity paused forever.
   */
  pauseKind?: 'manual' | 'auto';
}

export interface PositionSource {
  readonly name: string;

  /**
   * Mér-e akkor is, ha a felhasználó lezárja a telefont vagy másik appra vált.
   *
   * Böngészőben ez MINDIG hamis, és nem beállítás kérdése: sem az iOS, sem az
   * Android nem ad webes API-t háttér-helymeghatározásra. A felület ez alapján
   * figyelmezteti a felhasználót, hogy tartsa ébren a képernyőt.
   */
  readonly supportsBackground: boolean;

  /**
   * Megbízható-e az időbélyegek sorrendje.
   *
   * A natív háttérszolgáltatások ébredés után KÖTEGELVE szállítanak, és a
   * köteg tartalmazhat olyan mintát, amelynek időbélyege korábbi a már
   * feldolgozottnál. A rögzítő ezt amúgy is kezeli, de a diagnosztikához
   * hasznos tudni, melyik forrástól várható.
   */
  readonly ordered: boolean;

  /**
   * A mozgásforma a natív szolgáltatás energia- és aktivitási profiljához kell.
   *
   * A `resume` mondja meg a natív oldalnak, hogy ez egy MÁR ISMERT aktivitás
   * folytatása-e (a JS IndexedDB-ből visszaállított, félbehagyott futás), vagy
   * egy VADONATÚJ kezdés. A kettő között a natív, lezárt képernyő alatt is
   * tovább gyűjtő lemezes sor sorsa dől el: `resume: true` esetén a rajta
   * maradt pontokat át kell adni (`drain`), `resume: false` esetén viszont
   * EL KELL DOBNI — különben egy korábbi, be nem gyűjtött aktivitás maradéka
   * szivároghat be egy azzal semmilyen kapcsolatban nem álló, később indított
   * körbe (GRUNDO #42).
   */
  start(
    handlers: PositionHandlers,
    activityType?: ActivityType,
    activityState?: PositionActivityState,
    resume?: boolean,
  ): Promise<void>;
  /** Előtérben a pontos, szűrt recorder-állapotot átadja a natív élő értesítésnek. */
  syncActivity?(state: PositionActivityState): void | Promise<void>;
  /**
   * Csak a JavaScript-oldali feliratkozást bontja le.
   *
   * Natív iOS-en a WebView újratöltődhet úgy, hogy a Core Location mérésnek
   * tovább kell futnia. A teljes `stop()` ilyenkor adatvesztést okozna.
   * Böngészős forrásnál nincs külön háttérszolgáltatás, ezért ez opcionális.
   */
  detach?(): void | Promise<void>;
  /**
   * Háttérben a minták nem jutnak el a JS-hez, amíg a forrás sorát ki nem
   * ürítik. Ez kiüríti és átadja őket (`onSample`) — az automatikus szünet
   * háttérbeli ellenőrzése ezt hívja a csend-ág kiértékelése ELŐTT.
   */
  flushPending?(): Promise<void>;
  /** Van-e átadásra váró (pufferelt vagy a sorban lévő) minta — ilyenkor a csend nem bizonyíték. */
  hasPendingSamples?(): boolean;
  /** A forrás diagnosztikai eseményeinek fogadója (`tracking/trace.ts`). */
  traceSink?: (kind: TraceKind, data: Record<string, TraceValue>) => void;
  stop(): void | Promise<void>;
}
